import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import StaffPage from "@/pages/StaffPage";
import DashboardPage from "@/pages/DashboardPage";
import RecordPurchasePage from "@/pages/RecordPurchasePage";
import RecordExpensePage from "@/pages/RecordExpensePage";
import EditPurchasePage from "@/pages/EditPurchasePage";
import EditExpensePage from "@/pages/EditExpensePage";
import ViewPurchasePage from "@/pages/ViewPurchasePage";
import ViewExpensePage from "@/pages/ViewExpensePage";
import MySubmissionsPage from "@/pages/MySubmissionsPage";
import ReportsPage from "@/pages/ReportsPage";
import OwnerReportsPage from "@/pages/OwnerReportsPage";
import ReviewQueuePage from "@/pages/ReviewQueuePage";
import RecordPaymentPage from "@/pages/RecordPaymentPage";
import RecordZPage from "@/pages/RecordZPage";
import ZClosingsPage from "@/pages/ZClosingsPage";
import ViewZPage from "@/pages/ViewZPage";
import CashClosingFormPage from "@/pages/CashClosingFormPage";
import ViewCashClosingPage, { CashClosingsPage } from "@/pages/ViewCashClosingPage";
import PaymentsPage from "@/pages/PaymentsPage";
import ViewPaymentPage from "@/pages/ViewPaymentPage";
import { useFeatures } from "@/lib/features";
import "@/App.css";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-navy flex items-center justify-center">
      <div className="text-center">
        <img src="/grainmill-logo.jpg" alt="Grainmill" className="h-20 mx-auto mb-4 rounded-lg animate-pulse" />
        <p className="text-cream/50 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function Phase2Route({ children }) {
  const { phase2, loading } = useFeatures();
  if (loading) return <LoadingScreen />;
  return <ProtectedRoute>{phase2 ? children : <Navigate to="/dashboard" replace />}</ProtectedRoute>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/record-purchase" element={<ProtectedRoute><RecordPurchasePage /></ProtectedRoute>} />
      <Route path="/record-expense" element={<ProtectedRoute><RecordExpensePage /></ProtectedRoute>} />
      <Route path="/edit-purchase/:id" element={<ProtectedRoute><EditPurchasePage /></ProtectedRoute>} />
      <Route path="/edit-expense/:id" element={<ProtectedRoute><EditExpensePage /></ProtectedRoute>} />
      <Route path="/view-purchase/:id" element={<ProtectedRoute><ViewPurchasePage /></ProtectedRoute>} />
      <Route path="/view-expense/:id" element={<ProtectedRoute><ViewExpensePage /></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><ReviewQueuePage /></ProtectedRoute>} />
      <Route path="/submissions" element={<ProtectedRoute><MySubmissionsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><StaffPage /></ProtectedRoute>} />
      <Route path="/owner-reports" element={<Phase2Route><OwnerReportsPage /></Phase2Route>} />
      <Route path="/record-payment" element={<Phase2Route><RecordPaymentPage /></Phase2Route>} />
      <Route path="/payments" element={<Phase2Route><PaymentsPage /></Phase2Route>} />
      <Route path="/payments/:id" element={<Phase2Route><ViewPaymentPage /></Phase2Route>} />
      <Route path="/z-closings" element={<Phase2Route><ZClosingsPage /></Phase2Route>} />
      <Route path="/z-closings/:id" element={<Phase2Route><ViewZPage /></Phase2Route>} />
      <Route path="/record-z" element={<Phase2Route><RecordZPage /></Phase2Route>} />
      <Route path="/edit-z/:id" element={<Phase2Route><RecordZPage /></Phase2Route>} />
      <Route path="/cash-closings" element={<Phase2Route><CashClosingsPage /></Phase2Route>} />
      <Route path="/cash-closings/:id" element={<Phase2Route><ViewCashClosingPage /></Phase2Route>} />
      <Route path="/close-cash" element={<Phase2Route><CashClosingFormPage /></Phase2Route>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" offset={72} mobileOffset={{ top: "calc(env(safe-area-inset-top) + 68px)" }}
          toastOptions={{ style: { background: "#021F47", color: "#F1E8DF", border: "1px solid rgba(209,157,48,0.3)" } }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
